import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { settings } = useSettings();

  const [tokenInfo, setTokenInfo] = useState(null); // { email, name } or null
  const [tokenError, setTokenError] = useState("");
  const [validating, setValidating] = useState(true);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Validate the token on mount
  useEffect(() => {
    if (!token) { setTokenError("No reset token found. Please request a new link."); setValidating(false); return; }
    api.get(`/auth/reset-password/${token}`)
      .then(({ data }) => { setTokenInfo(data); setValidating(false); })
      .catch((err) => {
        setTokenError(formatApiErrorDetail(err.response?.data?.detail) || "This reset link is invalid or has expired.");
        setValidating(false);
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      await refresh();
      setDone(true);
      // Brief pause, then navigate to the right portal
      setTimeout(() => navigate("/"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--atech-bg)] px-4">
      <div className="w-full max-w-sm fade-up">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="font-display text-2xl text-[color:var(--atech-text)] mb-1">
            {settings.practice_name}
          </div>
          <div className="label text-[color:var(--atech-text-muted)]">{settings.portal_name}</div>
        </div>

        <div className="card-surface p-8">
          {validating ? (
            <div className="text-center py-6 label text-[color:var(--atech-text-muted)]">
              Validating link…
            </div>
          ) : tokenError ? (
            /* ── Invalid / expired token ── */
            <div className="text-center py-4" data-testid="reset-token-error">
              <div className="text-[#F87171] text-sm mb-4 leading-relaxed">{tokenError}</div>
              <Link
                to="/forgot-password"
                className="btn-copper inline-flex items-center gap-2 text-sm py-2.5 px-5"
                data-testid="request-new-link"
              >
                Request a new link
              </Link>
            </div>
          ) : done ? (
            /* ── Success ── */
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-12 h-12 rounded-full bg-[#34D399]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={22} className="text-[#34D399]" />
              </div>
              <h2 className="font-display text-xl text-[color:var(--atech-text)] mb-2">
                Password updated
              </h2>
              <p className="text-sm text-[color:var(--atech-text-dim)]">
                You're signed in. Redirecting you now…
              </p>
            </div>
          ) : (
            /* ── Password form ── */
            <>
              <h2 className="font-display text-xl text-[color:var(--atech-text)] mb-1">
                Choose a new password
              </h2>
              {tokenInfo?.name && (
                <p className="text-sm text-[color:var(--atech-text-dim)] mb-5">
                  Hi {tokenInfo.name.split(" ")[0]} — set a new password for{" "}
                  <span className="text-[color:var(--atech-text)]">{tokenInfo.email}</span>.
                </p>
              )}

              {error && (
                <div
                  className="text-[#F87171] text-sm mb-4 p-3 bg-[#F87171]/8 rounded-[2px]"
                  data-testid="reset-error"
                >
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <label className="label text-[color:var(--atech-text-muted)] mb-1.5 block">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      autoFocus
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={8}
                      placeholder="At least 8 characters"
                      className="w-full bg-[color:var(--atech-bg)] border border-[color:var(--atech-border)] rounded-[2px] px-4 py-3 pr-10 text-sm focus:outline-none focus:border-[color:var(--atech-copper)] transition-colors"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)]"
                    >
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="label text-[color:var(--atech-text-muted)] mb-1.5 block">
                    Confirm password
                  </label>
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat new password"
                    className="w-full bg-[color:var(--atech-bg)] border border-[color:var(--atech-border)] rounded-[2px] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--atech-copper)] transition-colors"
                    data-testid="reset-confirm-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-copper w-full py-3 text-sm disabled:opacity-60"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Saving…" : "Set new password"}
                </button>
              </form>
            </>
          )}
        </div>

        <Link
          to="/login"
          className="flex items-center justify-center gap-2 mt-6 text-xs text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)] transition-colors"
          data-testid="back-to-login-reset"
        >
          <ArrowLeft size={13} /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
